import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Badge, 
  Pagination, 
  Modal, 
  Form, 
  Alert,
  Spinner
} from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import Button from 'components/base/Button';
import SearchBox from 'components/common/SearchBox';
import { Contact, ContactType, ContactStatus, User, ContactsResponse, CreateContactPayload, UpdateContactPayload } from 'shared/types';
import { ContactsService } from 'shared/services/api/contacts';
import RestService from 'shared/services/http-client';
import ServiceList from 'shared/services/http-client/list';
import { useSelector } from 'react-redux';
import { 
  generateSecurePassword,
  getContactTypeBadgeVariant,
  getContactStatusBadgeVariant,
  formatContactStatus,
  validateContactForm,
  generateDefaultUsername,
  type ContactFormData
} from 'shared/helpers/contacts';
import { generatePaginationRange, calculatePaginationInfo } from 'shared/helpers/pagination';
import { updateUrlParams, clearAllUrlFilters, hasActiveFilters } from 'shared/helpers/url';
import { WithRoles } from 'components/auth';
import 'assets/scss/contacts-dropdown.scss';

const Contacts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    age: '',
    address: '',
    type: 'contact' as ContactType,
    status: 'pending' as ContactStatus,
    assigned: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [createAccountLoading, setCreateAccountLoading] = useState(false);

  const currentPage = parseInt(searchParams.get('page') || '1');
  const searchQuery = searchParams.get('search') || '';
  const typeFilter = searchParams.get('type') || 'contact'; // Default filter
  const statusFilter = searchParams.get('status') || '';
  const assignedFilter = searchParams.get('assigned') || '';
  const limit = 10;
  
  // Get user profile for customerId
  const userProfile = useSelector((state: any) => state.user?.profile);
  const customerId = userProfile?.customerId;

  // Fetch contacts 
  const fetchContacts = async () => {
    // Use fallback customerId if not available (for development)
    const activeCustomerId = customerId || 1;

    try {
      setLoading(true);
      
      // Parse type filter for multiple types
      const types = typeFilter.split(',').filter(Boolean);
      
      const response = await ContactsService.getContacts({
        page: currentPage,
        limit,
        search: searchQuery || undefined,
        type: types.length === 1 ? types[0] as ContactType : undefined,
        status: statusFilter as ContactStatus || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      
      setContacts(response.items);
      setTotal(response.items.length); // Note: Backend should return total count
      setTotalPages(Math.ceil(response.items.length / limit));
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      setContacts([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for assignment dropdown
  const fetchUsers = async () => {
    try {
      const response = await RestService.send({
        serviceName: ServiceList.getUsers
      });
      setUsers(response.data);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      
      // Fallback to mock data if API is not available
      if (error?.response?.status === 404) {
        console.warn('Users API not available, using mock data');
        const mockUsers = [
          { id: '1', name: 'John Doe', email: 'john@example.com', username: 'john' },
          { id: '2', name: 'Jane Smith', email: 'jane@example.com', username: 'jane' },
          { id: '3', name: 'Mike Johnson', email: 'mike@example.com', username: 'mike' }
        ];
        setUsers(mockUsers);
      } else {
        setUsers([]);
      }
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [currentPage, searchQuery, typeFilter, statusFilter, assignedFilter, customerId]);

  // useEffect(() => {
  //   fetchUsers();
  // }, []);

  // Handle search
  const handleSearch = (value: string) => {
    const params = updateUrlParams(searchParams, { search: value || null });
    setSearchParams(params);
  };

  // Handle filter changes
  const handleFilterChange = (filterType: string, value: string) => {
    const params = updateUrlParams(searchParams, { [filterType]: value || null });
    setSearchParams(params);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchParams(clearAllUrlFilters());
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    const params = updateUrlParams(searchParams, { page: page.toString() }, false);
    setSearchParams(params);
  };

  // Open modal for adding/editing
  const openModal = (contact?: Contact) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        name: contact.name,
        phoneNumber: contact.phoneNumber,
        email: contact.email,
        age: contact.age.toString(),
        address: contact.address,
        type: contact.type,
        status: contact.status,
        assigned: contact.assigned || ''
      });
    } else {
      setEditingContact(null);
      setFormData({
        name: '',
        phoneNumber: '',
        email: '',
        age: '',
        address: '',
        type: 'contact',
        status: 'pending',
        assigned: ''
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  // Save contact
  const handleSave = async () => {
    const errors = validateContactForm(formData);

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Use fallback customerId if not available (for development)
    const activeCustomerId = customerId || 1;

    try {
      setSaving(true);
      const contactData = {
        ...formData,
        age: parseInt(formData.age),
        customerId: activeCustomerId
      };

      if (editingContact) {
        const updateData: UpdateContactPayload = {
          name: contactData.name,
          phoneNumber: contactData.phoneNumber,
          email: contactData.email,
          age: contactData.age,
          address: contactData.address,
          type: contactData.type,
          status: contactData.status,
          assigned: contactData.assigned || undefined
        };
        await ContactsService.updateContact(editingContact.id, updateData);
      } else {
        const createData: CreateContactPayload = {
          customerId: contactData.customerId,
          name: contactData.name,
          phoneNumber: contactData.phoneNumber,
          email: contactData.email,
          age: contactData.age,
          address: contactData.address,
          type: contactData.type,
          status: contactData.status,
          assigned: contactData.assigned || undefined
        };
        await ContactsService.createContact(createData);
      }

      setShowModal(false);
      fetchContacts();
    } catch (error: any) {
      if (error.data?.errors?.fields) {
        setFormErrors(error.data.errors.fields);
      } else {
        console.error('Failed to save contact:', error);
      }
    } finally {
      setSaving(false);
    }
  };

  // Create account
  const handleCreateAccount = async (contact: Contact) => {
    try {
      setCreateAccountLoading(true);
      const username = generateDefaultUsername(contact.email, contact.phoneNumber);
      const password = generateSecurePassword();
      
      await ContactsService.createAccount(contact.id, { username, password });
      
      alert(`Account created!\nUsername: ${username}\nPassword: ${password}`);
      fetchContacts();
    } catch (error: any) {
      alert(error.message || 'Failed to create account');
    } finally {
      setCreateAccountLoading(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async (contact: Contact) => {
    if (!confirm('Are you sure you want to delete this account? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleteAccountLoading(true);
      await ContactsService.deleteAccount(contact.id);
      
      fetchContacts();
    } catch (error: any) {
      alert(error.message || 'Failed to delete account');
    } finally {
      setDeleteAccountLoading(false);
    }
  };


  return (
    <Container fluid>
      {/* Page Header */}
      <Row className="g-3 mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h2 className="mb-0 text-body-emphasis">Contacts</h2>
              <p className="text-body-tertiary mb-0 mt-1">
                Manage your contacts, leads, and their accounts
              </p>
            </div>
          </div>
        </Col>
      </Row>
      
      {/* Filters Row */}
      <Row className="g-3 mb-4 contacts-filters">
        <Col lg={3} md={4}>
          <SearchBox
            placeholder="Search by name, phone, or email..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="h-100 w-100"
          />
        </Col>
        <Col lg={2} md={3} sm={6}>
          <Form.Select
            value={typeFilter}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="fw-semibold"
          >
            <option value="contact">Contact Only</option>
            <option value="lead">Lead Only</option>
            <option value="archive">Archive Only</option>
            <option value="contact,lead">Contact & Lead</option>
            <option value="contact,lead,archive">All Types</option>
          </Form.Select>
        </Col>
        <Col lg={2} md={3} sm={6}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="fw-semibold"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="in_process">In Process</option>
            <option value="conversion">Conversion</option>
            <option value="failure">Failure</option>
          </Form.Select>
        </Col>
        <Col lg={2} md={3} sm={6}>
          <Form.Select
            value={assignedFilter}
            onChange={(e) => handleFilterChange('assigned', e.target.value)}
            className="fw-semibold"
          >
            <option value="">All Assigned</option>
            <option value="unassigned">Unassigned</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col lg={3} md={3} sm={6} className="text-end">
          <div className="d-flex gap-2 justify-content-end">
            {hasActiveFilters(searchQuery, typeFilter, statusFilter, assignedFilter) && (
              <Button 
                variant="phoenix-secondary" 
                size="sm"
                onClick={clearAllFilters}
                startIcon={<FeatherIcon icon="x" size={14} />}
              >
                Clear
              </Button>
            )}
            <WithRoles 
              roles={[1, 2]}
              fallback={
                <Button 
                  variant="secondary" 
                  disabled
                  title="Only admins and managers can add contacts"
                  startIcon={<FeatherIcon icon="plus" size={16} />}
                >
                  Add Contact
                </Button>
              }
            >
              <Button 
                variant="primary" 
                onClick={() => openModal()}
                startIcon={<FeatherIcon icon="plus" size={16} />}
              >
                Add Contact
              </Button>
            </WithRoles>
          </div>
        </Col>
      </Row>

      {/* Main Content Card */}
      <Row className="g-3">
        <Col>
          <Card className="h-100">
            <Card.Header className="bg-body-emphasis border-bottom border-translucent">
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="mb-0 text-body-emphasis fw-semibold">
                  Contact List
                </h6>
                <span className="text-body-tertiary fs-10">
                  {total} total contacts
                </span>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : contacts.length === 0 ? (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <FeatherIcon icon="users" size={48} className="text-body-tertiary" />
                </div>
                  <h6 className="text-body-emphasis mb-2">No contacts found</h6>
                  <p className="text-body-tertiary mb-3">
                    {searchQuery || statusFilter || assignedFilter || typeFilter !== 'contact' 
                      ? 'Try adjusting your filters to see more results.'
                      : 'Get started by adding your first contact.'
                    }
                  </p>
                  {(!searchQuery && !statusFilter && !assignedFilter && typeFilter === 'contact') && (
                    <Button 
                      variant="primary" 
                      onClick={() => openModal()}
                      startIcon={<FeatherIcon icon="plus" size={16} />}
                    >
                      Add First Contact
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table className="table-sm table-borderless fs-10 mb-0">
                      <thead className="bg-body-secondary">
                        <tr>
                          <th className="text-body-emphasis fw-semibold py-3 ps-4">Name</th>
                          <th className="text-body-emphasis fw-semibold py-3">Phone</th>
                          <th className="text-body-emphasis fw-semibold py-3">Email</th>
                          <th className="text-body-emphasis fw-semibold py-3">Age</th>
                          <th className="text-body-emphasis fw-semibold py-3">Address</th>
                          <th className="text-body-emphasis fw-semibold py-3">Type</th>
                          <th className="text-body-emphasis fw-semibold py-3">Status</th>
                          <th className="text-body-emphasis fw-semibold py-3">Assigned</th>
                          <th className="text-body-emphasis fw-semibold py-3">Created At</th>
                          <th className="text-body-emphasis fw-semibold py-3 pe-4 text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((contact, index) => (
                          <tr 
                            key={contact.id}
                            className={`border-bottom border-translucent ${
                              index % 2 === 0 ? 'bg-body-highlight' : ''
                            }`}
                          >
                            <td className="ps-4 py-3">
                              <div className="d-flex align-items-center">
                                <span className="fw-semibold text-body-emphasis fs-9">
                                  {contact.name}
                                </span>
                              </div>
                            </td>
                            <td className="py-3">
                              <a 
                                href={`tel:${contact.phoneNumber}`}
                                className="text-decoration-none text-primary fw-medium fs-9 d-flex align-items-center"
                                title={`Call ${contact.phoneNumber}`}
                              >
                                <FeatherIcon icon="phone" size={12} className="me-2 text-body-tertiary" />
                                {contact.phoneNumber}
                              </a>
                            </td>
                            <td className="py-3">
                              <a 
                                href={`mailto:${contact.email}`}
                                className="text-decoration-none text-primary fw-medium fs-9 d-flex align-items-center"
                                title={`Email ${contact.email}`}
                              >
                                <FeatherIcon icon="mail" size={12} className="me-2 text-body-tertiary" />
                                <span className="text-truncate" style={{maxWidth: '150px'}}>
                                  {contact.email}
                                </span>
                              </a>
                            </td>
                            <td className="py-3">
                              <span className="text-body-emphasis fw-medium fs-9">{contact.age}</span>
                            </td>
                            <td className="py-3">
                              <span 
                                className="text-body fs-9 text-truncate d-inline-block" 
                                style={{maxWidth: '200px'}}
                                title={contact.address}
                              >
                                {contact.address}
                              </span>
                            </td>
                            <td className="py-3">
                              <Badge 
                                bg={getContactTypeBadgeVariant(contact.type)}
                                className="fs-11"
                              >
                                {contact.type}
                              </Badge>
                            </td>
                            <td className="py-3">
                              <Badge 
                                bg={getContactStatusBadgeVariant(contact.status)}
                                className="fs-11"
                              >
                                {formatContactStatus(contact.status)}
                              </Badge>
                            </td>
                            <td className="py-3">
                              {contact.assigned ? (
                                <span className="text-body-emphasis fw-medium fs-9">
                                  {users.find(u => u.id === contact.assigned)?.name || contact.assigned}
                                </span>
                              ) : (
                                <span className="text-body-tertiary fs-9">Unassigned</span>
                              )}
                            </td>
                            <td className="py-3">
                              <span className="text-body fs-9">
                                {new Date(contact.createdAt).toLocaleDateString('en-GB')}
                              </span>
                            </td>
                            <td className="py-3 pe-4 text-end">
                              <Button
                                variant="phoenix-secondary"
                                size="sm"
                                onClick={() => openModal(contact)}
                                className="btn-sm px-2 py-1"
                              >
                                <FeatherIcon icon="edit" size={12} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (() => {
                    const paginationInfo = calculatePaginationInfo(currentPage, limit, total);
                    const { pages } = generatePaginationRange(currentPage, totalPages);
                    
                    return (
                      <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top border-translucent">
                        <div className="text-body-tertiary fs-10">
                          Showing <span className="fw-semibold text-body-emphasis">{paginationInfo.start}</span> to{' '}
                          <span className="fw-semibold text-body-emphasis">{paginationInfo.end}</span> of{' '}
                          <span className="fw-semibold text-body-emphasis">{paginationInfo.total}</span> entries
                </div>
                        <Pagination size="sm" className="mb-0">
                          <Pagination.First 
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(1)}
                            className="border-0"
                          />
                          <Pagination.Prev 
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="border-0"
                          />
                          
                          {pages.map((page, index) => 
                            page === '...' ? (
                              <Pagination.Ellipsis key={`ellipsis-${index}`} disabled className="border-0" />
                            ) : (
                              <Pagination.Item
                                key={page}
                                active={page === currentPage}
                                onClick={() => handlePageChange(page as number)}
                                className="border-0"
                              >
                                {page}
                              </Pagination.Item>
                            )
                          )}
                          
                          <Pagination.Next
                            disabled={currentPage === totalPages}
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="border-0"
                          />
                          <Pagination.Last
                            disabled={currentPage === totalPages}
                            onClick={() => handlePageChange(totalPages)}
                            className="border-0"
                          />
                        </Pagination>
              </div>
                    );
                  })()}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Contact Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="border-bottom border-translucent">
          <Modal.Title className="text-body-emphasis fw-bold">
            {editingContact ? 'Edit Contact' : 'Add Contact'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Row className="g-3 contacts-filters">
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="text-body-emphasis fw-semibold fs-9 mb-2">
                  Name *
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  isInvalid={!!formErrors.name}
                  className="fw-semibold"
                  placeholder="Enter full name"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="text-body-emphasis fw-semibold fs-9 mb-2">
                  Phone *
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                  isInvalid={!!formErrors.phoneNumber}
                  className="fw-semibold"
                  placeholder="+84 901 234 567"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.phoneNumber}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="text-body-emphasis fw-semibold fs-9 mb-2">
                  Email *
                </Form.Label>
                <Form.Control
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  isInvalid={!!formErrors.email}
                  className="fw-semibold"
                  placeholder="email@example.com"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="text-body-emphasis fw-semibold fs-9 mb-2">
                  Age *
                </Form.Label>
                <Form.Control
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({...formData, age: e.target.value})}
                  isInvalid={!!formErrors.age}
                  className="fw-semibold"
                  placeholder="25"
                  min="1"
                  max="150"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.age}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-0">
                <Form.Label className="text-body-emphasis fw-semibold fs-9 mb-2">
                  Address *
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  isInvalid={!!formErrors.address}
                  className="fw-semibold"
                  placeholder="Enter full address"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.address}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-0">
                <Form.Label className="text-body-emphasis fw-semibold fs-9 mb-2">
                  Type
                </Form.Label>
                <Form.Select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value as ContactType})}
                  className="fw-semibold"
                >
                  <option value="contact">Contact</option>
                  <option value="lead">Lead</option>
                  <option value="archive">Archive</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-0">
                <Form.Label className="text-body-emphasis fw-semibold fs-9 mb-2">
                  Status
                </Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value as ContactStatus})}
                  className="fw-semibold"
                >
                  <option value="pending">Pending</option>
                  <option value="in_process">In Process</option>
                  <option value="conversion">Conversion</option>
                  <option value="failure">Failure</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-0">
                <Form.Label className="text-body-emphasis fw-semibold fs-9 mb-2">
                  Assigned
                </Form.Label>
                <Form.Select
                  value={formData.assigned}
                  onChange={(e) => setFormData({...formData, assigned: e.target.value})}
                  className="fw-semibold"
                >
                  <option value="">Unassigned</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <WithRoles roles={[1]}>
            {editingContact && (
              <div className="border-top border-translucent pt-4 mt-4">
                <h6 className="text-body-emphasis fw-bold mb-3">Account Management</h6>
                <div className="d-flex gap-2">
                  {editingContact.hasAccount ? (
                    <Button
                      variant="phoenix-danger"
                      size="sm"
                      onClick={() => handleDeleteAccount(editingContact)}
                      loading={deleteAccountLoading}
                      startIcon={<FeatherIcon icon="trash-2" size={14} />}
                    >
                      Delete Account
                    </Button>
                  ) : (
                    <Button
                      variant="phoenix-success"
                      size="sm"
                      onClick={() => handleCreateAccount(editingContact)}
                      loading={createAccountLoading}
                      startIcon={<FeatherIcon icon="user-plus" size={14} />}
                    >
                      Create Account
                    </Button>
                  )}
                </div>
              </div>
            )}
          </WithRoles>
        </Modal.Body>
        <Modal.Footer className="border-top border-translucent">
          <Button 
            variant="phoenix-secondary" 
            onClick={() => setShowModal(false)}
            className="fw-semibold"
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSave} 
            loading={saving}
            className="fw-semibold"
          >
            {editingContact ? 'Update Contact' : 'Create Contact'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Contacts;
